import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ElNotification } from 'element-plus'
import { getAllPermissions, requestPermission } from '@/utils/permissions'
import { useDataStore } from '@/stores/data'
import { log } from '@/utils/logger'
import { promptModal } from '@/composables/promptModal'
import { useAnalyticsEvents } from '@/composables/useAnalyticsEvents'

export const useAppStore = defineStore('app', {
	state: () => ({
		applicationId: 'no.opinion.reisevaner',
		version: '1.0.37',
		version_date: '2025-11-20',
		bottomSheet: ref(null),
		permissions: {
			notification: 0,
			bluetooth: 0,
			location_wheninuse: 0,
			location_always: 0,
			motion: 0,
		},
		onboardedPermissions: false,
		onboardedSurvey: false,
		surveyCompleted: false,
		currentPage: '',
		previousPage: '',
		navigatedBack: false,
		initializing: false,
		hidesplash: false,
		loading: false,
		loaded: false,
		updating: false,
		editStep: '',
		neededConsents: ['surveys', 'analytics'],
		tracking: false,
		trackingTimeout: false,
		trackingResumedTimestamp: null,
		editHide: false,
		consents: [],
		expandedHelp: false,
		expandedProfile: false,
		expandedDeveloperOptions: false,
		futureRelease: false,
		splitIndex: -1,
		showSplitLocation: false,
		isPreview: document.location.search.includes('preview'),
		isFrame: document.location.search.includes('iframe'),
		debug: false,
		hideSplashTimeout: null,
		handleInitTimeout: null,
		finishSplashTimeout: null,
		events: useAnalyticsEvents(),
		consentsGrantedDate: null,
		consentsRevokedDate: null,
	}),
	getters: {
		availableDatePicker() {
			const dataStore = useDataStore()
			return Object.keys(dataStore.availableDates).length > 0
		},
		invalidPermissions() {
			const isValidConsents = this.hasNeededConsents()
			const isInvalid = this.permissions.notification == 0 || this.permissions.bluetooth < 1 || this.permissions.location_wheninuse < 1 || this.permissions.location_always < 1 || this.permissions.motion < 1 || !isValidConsents
			console.warn('invalidPermissions', isInvalid, this.permissions, 'isValidConsents', isValidConsents)
			if (isInvalid) this.onboardedPermissions = false
			return isInvalid
		},
	},
	actions: {
		async initApp(source = '') {
			log('appStore: initApp', source).white()
			setLoad('app.js:initApp', 6)
			// Load saved states for datastore
			const dataStore = useDataStore()

			// Load saved states
			await dataStore.loadStates()

			// Set user properties for firebase
			this.events.setUserProperties()

			setLoad('app.js:initApp', 15)

			// Make sure onboardedPermissions is false when invalidPermissions is true
			if (this.invalidPermissions) this.onboardedPermissions = false

			setLoad('app.js:initApp', 16)

			this.loaded = false
			console.warn('app:initApp', source, this.onboardedSurvey, this.onboardedPermissions, this.invalidPermissions)
			if ((!this.onboardedSurvey || !this.onboardedPermissions) && !(this.onboardedPermissions && this.invalidPermissions)) {
				this.setPage('onboarding')
				this.loaded = true
				dataStore.flyToUserLocation(12)
			} else {
				if (this.invalidPermissions) {
					if (this.trackingTimeout) this.setPage('onboarding')
					else this.setPage('permissions')
					this.loaded = true
				} else {
					await dataStore.loadAndProcessData(true)
				}
			}
		},

		async init() {
			setLoad('app.js:init', 4)
			console.log('App store init')
			this.getConsents()
			this.getPermissions()
		},
		async loadSavedStates() {
			this.onboardedPermissions = (await kettle.get('onboarded_permissions')) === 'true'
			this.onboardedSurvey = (await kettle.get('onboarded_survey')) === 'true'
			this.tracking = (await kettle.get('tracking')) === 'true'
			this.trackingTimeout = (await kettle.get('tracking_timeout')) === 'true'
			this.trackingResumedTimestamp = await kettle.get('tracking_resumed_timestamp')
			this.consentsGrantedDate = await kettle.get('consents_granted_date')
			this.consentsRevokedDate = await kettle.get('consents_revoked_date')
		},
		hasNeededConsents() {
			return this.neededConsents.every((c) => this.consents.includes(c))
		},
		async getPermissions() {
			this.permissions = await getAllPermissions(this.permissions)
			return this.permissions
		},
		async requestPermission(permissionType) {
			return await requestPermission(permissionType)
		},
		async toggleConsents() {
			var consents = await kettle.getConsents()
			var allConsentsGranted = this.neededConsents.every((c) => consents.includes(c))

			if (allConsentsGranted) {
				this.consents = await this.revokeConsents()
				this.stopTracking()
			} else {
				this.consents = await this.grantConsents()
				this.startTracking()
			}
		},
		async getConsents() {
			this.consents = await kettle.getConsents()

			// Also get timestamps for granted and revoked consents
			this.consentsGrantedDate = await kettle.get('consents_granted_date')
			this.consentsRevokedDate = await kettle.get('consents_revoked_date')

			// Add current date as default to be compatible with older versions without timestamps
			if (this.hasNeededConsents() && !this.consentsGrantedDate) {
				this.consentsGrantedDate = new Date().toISOString()
				kettle.set('consents_granted_date', this.consentsGrantedDate)
			}

			return this.consents
		},
		async grantConsents() {
			this.consentsGrantedDate = new Date().toISOString()
			this.consents = await kettle.grantConsents([...this.neededConsents])
			kettle.set('consents_granted_date', this.consentsGrantedDate)
			kettle.remove('tracking_timeout')
			this.startTracking()
			return this.consents
		},
		async revokeConsents() {
			this.consentsRevokedDate = new Date().toISOString()
			var consents = await kettle.getConsents()
			this.consents = await kettle.revokeConsents(consents)
			kettle.set('consents_revoked_date', this.consentsRevokedDate)
			this.stopTracking()
			return this.consents
		},
		goBack() {
			this.setPage(this.previousPage, 'goBack', true)
		},
		setPage(pageName, source = '', didGoBack = false) {
			var info = ''
			if (this.currentPage) info += 'currentPage: ' + this.currentPage
			if (source) info += ' source: ' + source
			log('setPage', pageName, info).white()
			this.previousPage = this.currentPage
			this.currentPage = pageName
			this.navigatedBack = didGoBack
		},
		async showConfirm(title, message) {
			const { dialogState, handleConfirm } = promptModal()
			dialogState.title = title
			dialogState.message = message
			dialogState.mode = 'confirm'
			dialogState.inputValue = ''
			dialogState.resolvePromise = handleConfirm // Store the resolve function
			dialogState.isVisible = true

			return new Promise((resolve) => {
				dialogState.resolvePromise = resolve // Store the resolve function
			})
		},
		toast(options) {
			ElNotification.closeAll()
			ElNotification(options)
		},
		futureReleaseToast() {
			this.toast({ title: 'Ikke implementert', message: 'Løsningen er under utvikling - funksjonen kommer i senere versjon', type: 'error', duration: 10000 })
		},
		setBottomSheetRef(ref) {
			this.bottomSheet = ref
		},
		startTracking() {
			kettle.startModules(['location', 'activity', 'bluetooth'])
			kettle.set('tracking', JSON.stringify(true))
			this.tracking = true
			console.warn('startTracking', this.tracking)
		},
		stopTracking() {
			kettle.stopModules(['location', 'activity', 'bluetooth'])
			kettle.set('tracking', JSON.stringify(false))
			this.tracking = false
			console.warn('stopTracking', this.tracking)
		},
		startInit(clicked = false) {
			console.log('App store startInit')
			const dataStore = useDataStore()
			if (clicked) {
				setTimeout(() => {
					document.location.reload()
				}, 1000)
			} else {
				setTimeout(() => {
					dataStore.selectedDate = dataStore.yesterDate
				}, 500)
			}
			this.initializing = true
		},
		reload() {
			setTimeout(() => {
				document.location.reload()
			}, 200)
		},
		showSplash() {
			console.warn('App is initializing')
			this.hidesplash = false
			var self = this
			clearTimeout(this.handleInitTimeout)
			// Handled from onMounted
			// this.handleInitTimeout = setTimeout(() => {
			// 	self.initApp('showsplash')
			// }, 1000)
			clearTimeout(this.hideSplashTimeout)
			this.hideSplashTimeout = setTimeout(() => {
				self.hidesplash = true
			}, 1800)
			clearTimeout(this.finishSplashTimeout)
			this.finishSplashTimeout = setTimeout(() => {
				self.initializing = false
				self.hidesplash = false
			}, 3000)
		},
		async messageHandler(event) {
			// Listen for post message to this iframe
			const dataStore = useDataStore()
			console.warn('message', event.data)
			if (event.data == 'resetSurvey') {
				if (dataStore.respondentId) {
					dataStore.resetSurvey()
					this.reload()
				} else alert('Kunne ikke utføre sletting da bruker ikke er identifisert')
			}
			if (event.data == 'clearPermissions') {
				dataStore.clearPermissions()
				this.reload()
			}
			if (event.data === 'reload') {
				this.reload()
			}
			if (event.data === 'logout') {
				dataStore.clearUser()
				this.reload()
			}
			if (event.data === 'clearStates') {
				dataStore.clearStates()
				this.reload()
			}
			if (event.data === 'clearAll') {
				await dataStore.clearAll()
				this.reload()
			}
			if (event.data === 'clearTrips') {
				await dataStore.clearTrips()
				this.reload()
			}
			if (event.data === 'toggle_loction_always') {
				appStore.permissions.location_always = !appStore.permissions.location_always
				if (appStore.permissions.location_always == 0) localStorage.removeItem('location_always')
				else localStorage['location_always'] = appStore.permissions.location_always ? 'granted' : 'denied'
			}
			if (event.data.type === 'togglePermission') {
				console.warn('togglePermission', event.data)
				if (appStore.permissions[event.data.permission] == 0) {
					appStore.permissions[event.data.permission] = 1
					localStorage[event.data.permission] = 'granted'
				} else {
					if (appStore.permissions[event.data.permission] == 1) {
						appStore.permissions[event.data.permission] = -1
						localStorage[event.data.permission] = 'denied'
					} else {
						appStore.permissions[event.data.permission] = 0
						localStorage.removeItem(event.data.permission)
					}
				}
			}
			if (event.data.type === 'toggleFeature') {
				const dataStore = useDataStore()
				var featurePropertyName = event.data.feature
				if (dataStore.hasOwnProperty(featurePropertyName)) {
					dataStore[featurePropertyName] = !dataStore[featurePropertyName]
					kettle.set(featurePropertyName, JSON.stringify(dataStore[featurePropertyName]))
					console.warn('kettle.set', featurePropertyName, dataStore[featurePropertyName])
				}
				// Update directly in parent window
				if (window.self !== window.top) {
					window.parent.updateFeatures({
						userCanNavigateTrips: dataStore.userCanNavigateTrips,
						userCanChangeDates: dataStore.userCanChangeDates,
						userCanUndo: dataStore.userCanUndo,
					})
				}
			}
		},
	},
})
